import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { message } from 'antd';

const DEFAULT_TABS = [
  { id: 'logs', name: '消费明细', endpoint: '/v1/token/cost/logs' },
  { id: 'summary', name: '消费总计', endpoint: '/v1/token/cost/summary' },
  { id: 'models', name: '按模型统计', endpoint: '/v1/token/cost/models' },
];

const DOCS = {
  logs: {
    title: '消费明细查询',
    description:
      '适合排查某次调用花费了多少、用了哪个模型、走了哪个接口，以及单次请求的耗时表现。',
    endpoint: '/v1/token/cost/logs',
    tips: [
      '只返回当前分发 token 的消费记录，不能跨 token 查询。',
      '默认查询最近 30 天；支持按时间范围和模型名精确筛选。',
      '适合用于对账、问题排查、给下游用户展示调用流水。',
    ],
  },
  summary: {
    title: '消费总计查询',
    description:
      '适合做账单概览，默认查看当前 token 从创建至今的总请求数、总 token 数和 USD/CNY 双币种累计金额，也支持指定时间范围。',
    endpoint: '/v1/token/cost/summary',
    tips: [
      '返回总金额、请求次数、输入输出 token、总 token 和当前 token 的双币种额度限制。',
      '时间留空时默认查询从 token 创建至今的累计消费；填写时间可查询指定区间。',
      '如果你需要给客户展示月度或周度账单，这个接口最合适。',
    ],
  },
  models: {
    title: '按模型统计',
    description:
      '适合分析当前 token 最常用的模型、各模型的 token 消耗和金额占比，方便优化成本结构。',
    endpoint: '/v1/token/cost/models',
    tips: [
      '按模型聚合当前 token 的请求量、token 数和双币种金额。',
      '可配合 model_name 做单模型聚焦查询。',
      '适合做排行榜、占比图、资源倾斜分析。',
    ],
  },
};

const RESULT_VIEWS = [
  { id: 'table', label: '结构化' },
  { id: 'json', label: 'JSON' },
];

const EXAMPLE_TABS = [
  { id: 'curl', label: 'cURL' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'python', label: 'Python' },
];

const formatTime = (value) => {
  if (!value) return '-';
  const ts = Number(value) * 1000;
  if (Number.isNaN(ts)) return String(value);
  return new Date(ts).toLocaleString();
};

const toDateTimeLocalValue = (seconds) => {
  if (!seconds) return '';
  const date = new Date(Number(seconds) * 1000);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromDateTimeLocalValue = (value) => {
  if (!value) return '';
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return '';
  return String(Math.floor(ms / 1000));
};

const CostQueryPanel = ({ category }) => {
  const tabs = category?.tabs?.length ? category.tabs : DEFAULT_TABS;
  const [activeTab, setActiveTab] = useState(tabs[0]?.id || 'logs');
  const [exampleTab, setExampleTab] = useState('curl');
  const [resultView, setResultView] = useState('table');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    token: '',
    start_time: '',
    end_time: '',
    start_time_local: '',
    end_time_local: '',
    model_name: '',
    page: '0',
    page_size: '20',
  });

  useEffect(() => {
    setActiveTab(tabs[0]?.id || 'logs');
  }, [category, tabs]);

  const activeDoc = DOCS[activeTab] || DOCS.logs;
  const currentEndpoint = activeDoc.endpoint;

  const queryParams = useMemo(() => {
    const params = {};
    ['start_time', 'end_time', 'model_name'].forEach((key) => {
      const value = form[key]?.trim();
      if (value) params[key] = value;
    });
    if (activeTab === 'logs') {
      if (form.page?.trim()) params.page = form.page.trim();
      if (form.page_size?.trim()) params.page_size = form.page_size.trim();
    }
    return params;
  }, [activeTab, form]);

  const requestPreview = useMemo(() => {
    const query = new URLSearchParams(queryParams).toString();
    return query ? `${currentEndpoint}?${query}` : currentEndpoint;
  }, [currentEndpoint, queryParams]);

  const examplePayload = useMemo(() => {
    const query = new URLSearchParams(queryParams).toString();
    const url = query ? `${currentEndpoint}?${query}` : currentEndpoint;
    return {
      curl: `curl --location '${window.location.origin}${url}' \\
--header 'Authorization: Bearer YOUR_DISTRIBUTED_TOKEN'`,
      javascript: `import axios from 'axios';

const response = await axios.get('${url}', {
  headers: {
    Authorization: 'Bearer YOUR_DISTRIBUTED_TOKEN',
  },
});

console.log(response.data);`,
      python: `import requests

response = requests.get(
    '${window.location.origin}${url}',
    headers={'Authorization': 'Bearer YOUR_DISTRIBUTED_TOKEN'}
)
print(response.json())`,
    };
  }, [currentEndpoint, queryParams]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleDateTimeChange = (key, value) => {
    const timeKey = key === 'start_time_local' ? 'start_time' : 'end_time';
    setForm((prev) => ({
      ...prev,
      [key]: value,
      [timeKey]: fromDateTimeLocalValue(value),
    }));
  };

  const handleCopyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label}已复制`);
    } catch (error) {
      message.error(`复制${label}失败`);
    }
  };

  const handleFillExample = () => {
    const now = Math.floor(Date.now() / 1000);
    const sevenDaysAgo = now - 7 * 24 * 3600;
    setForm((prev) => ({
      ...prev,
      start_time: String(sevenDaysAgo),
      end_time: String(now),
      start_time_local: toDateTimeLocalValue(sevenDaysAgo),
      end_time_local: toDateTimeLocalValue(now),
      model_name: prev.model_name || '',
      page: '0',
      page_size: '20',
    }));
    message.success('已填充最近 7 天的查询示例');
  };

  const handleClear = () => {
    setForm({
      token: '',
      start_time: '',
      end_time: '',
      start_time_local: '',
      end_time_local: '',
      model_name: '',
      page: '0',
      page_size: '20',
    });
    setResult(null);
  };

  const handleQuery = async () => {
    const token = form.token.trim();
    if (!token) {
      message.error('请先填写分发 token');
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const response = await axios.get(currentEndpoint, {
        params: queryParams,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setResult(response.data);
      if (response.data?.success === false) {
        message.warning(response.data.message || '查询已返回错误信息');
      } else {
        message.success('查询成功');
      }
    } catch (error) {
      const errorPayload = {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error?.message ||
          error.message,
        raw: error?.response?.data || null,
      };
      setResult(errorPayload);
      message.error(errorPayload.message || '查询失败');
    } finally {
      setLoading(false);
    }
  };

  const renderSummaryCards = () => {
    const data = result?.data;
    if (!data) return null;
    const cards = [
      ['USD 总金额', data.total_amount_usd],
      ['CNY 总金额', data.total_amount_cny],
      ['USD 额度限制', data.remain_quota_usd],
      ['CNY 额度限制', data.remain_quota_cny],
      ['请求次数', data.request_count],
      ['输入 Token', data.prompt_tokens],
      ['输出 Token', data.completion_tokens],
      ['总 Token', data.total_tokens],
    ];
    return (
      <div className='cost-summary-grid'>
        {cards.map(([label, value]) => (
          <div key={label} className='cost-summary-card'>
            <div className='cost-summary-label'>{label}</div>
            <div className='cost-summary-value'>{value ?? '-'}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderLogsTable = () => {
    const rows = result?.data || [];
    if (!Array.isArray(rows) || rows.length === 0) {
      return <div className='cost-empty'>当前没有符合条件的消费记录。</div>;
    }
    return (
      <div className='cost-table-wrap'>
        <table className='cost-table'>
          <thead>
            <tr>
              <th>时间</th>
              <th>请求 ID</th>
              <th>模型</th>
              <th>接口</th>
              <th>输入</th>
              <th>输出</th>
              <th>币种</th>
              <th>金额</th>
              <th>耗时</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.request_id || `${row.created_at}-${row.model_name}`}>
                <td>{formatTime(row.created_at)}</td>
                <td className='mono'>{row.request_id || '-'}</td>
                <td>{row.model_name || '-'}</td>
                <td className='mono'>{row.api || '-'}</td>
                <td>{row.prompt_tokens ?? 0}</td>
                <td>{row.completion_tokens ?? 0}</td>
                <td>{row.currency || '-'}</td>
                <td>{row.amount ?? 0}</td>
                <td>{row.execute_time ?? 0} ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderModelsTable = () => {
    const rows = result?.data || [];
    if (!Array.isArray(rows) || rows.length === 0) {
      return <div className='cost-empty'>当前没有符合条件的模型统计结果。</div>;
    }
    return (
      <div className='cost-table-wrap'>
        <table className='cost-table'>
          <thead>
            <tr>
              <th>模型</th>
              <th>请求次数</th>
              <th>输入 Token</th>
              <th>输出 Token</th>
              <th>总 Token</th>
              <th>USD 总额</th>
              <th>CNY 总额</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.model_name}>
                <td>{row.model_name || '-'}</td>
                <td>{row.request_count ?? 0}</td>
                <td>{row.prompt_tokens ?? 0}</td>
                <td>{row.completion_tokens ?? 0}</td>
                <td>{row.total_tokens ?? 0}</td>
                <td>{row.total_amount_usd ?? 0}</td>
                <td>{row.total_amount_cny ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderResultTable = () => {
    if (!result) {
      return (
        <div className='cost-empty'>
          点击“立即查询”后，这里会显示结构化结果和原始 JSON。
        </div>
      );
    }
    if (result.success === false) {
      return (
        <div className='cost-error-box'>
          <div className='cost-error-title'>查询失败</div>
          <div>{result.message || '接口返回了错误信息'}</div>
        </div>
      );
    }
    if (activeTab === 'summary') return renderSummaryCards();
    if (activeTab === 'models') return renderModelsTable();
    return renderLogsTable();
  };

  return (
    <div className='playground-content-panel cost-query-panel'>
      <div className='cost-hero'>
        <div>
          <div className='cost-eyebrow'>TOKEN SELF-SERVICE · COST EXPLORER</div>
          <h1>费用查询</h1>
          <p>
            用分发 token 直接查看当前 token 的消费明细、总计和按模型统计。
            这块面向下游用户，不需要登录后台，也不会暴露其他 token 的账单数据。
          </p>
        </div>
        <div className='cost-hero-badges'>
          <span>{activeTab === 'summary' ? '默认累计消费' : '默认最近 30 天'}</span>
          <span>Bearer Token 鉴权</span>
          <span>只看当前 token</span>
        </div>
      </div>

      <div className='cost-mode-switch'>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type='button'
            className={`cost-mode-chip ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.name}</span>
            <small>{tab.endpoint}</small>
          </button>
        ))}
      </div>

      <div className='cost-layout'>
        <section className='cost-query-card'>
          <div className='cost-card-header'>
            <div>
              <h2>{activeDoc.title}</h2>
              <p>{activeDoc.description}</p>
            </div>
            <div className='cost-endpoint-tag'>{activeDoc.endpoint}</div>
          </div>

          <div className='cost-form-grid'>
            <label className='cost-field cost-field-wide'>
              <span>分发 Token</span>
              <input
                type='password'
                value={form.token}
                onChange={(e) => handleChange('token', e.target.value)}
                placeholder='填写 Bearer 后面的分发 token'
              />
            </label>
            <label className='cost-field'>
              <span>开始时间</span>
              <input
                type='datetime-local'
                value={form.start_time_local}
                onChange={(e) =>
                  handleDateTimeChange('start_time_local', e.target.value)
                }
              />
            </label>
            <label className='cost-field'>
              <span>结束时间</span>
              <input
                type='datetime-local'
                value={form.end_time_local}
                onChange={(e) =>
                  handleDateTimeChange('end_time_local', e.target.value)
                }
              />
            </label>
            <label className='cost-field'>
              <span>start_time</span>
              <input
                type='text'
                value={form.start_time}
                onChange={(e) => handleChange('start_time', e.target.value)}
                placeholder='秒级 Unix 时间戳'
              />
            </label>
            <label className='cost-field'>
              <span>end_time</span>
              <input
                type='text'
                value={form.end_time}
                onChange={(e) => handleChange('end_time', e.target.value)}
                placeholder='秒级 Unix 时间戳'
              />
            </label>
            <label className='cost-field cost-field-wide'>
              <span>model_name</span>
              <input
                type='text'
                value={form.model_name}
                onChange={(e) => handleChange('model_name', e.target.value)}
                placeholder='例如 gpt-4o-mini，可留空'
              />
            </label>
            {activeTab === 'logs' && (
              <>
                <label className='cost-field'>
                  <span>page</span>
                  <input
                    type='number'
                    min='0'
                    value={form.page}
                    onChange={(e) => handleChange('page', e.target.value)}
                  />
                </label>
                <label className='cost-field'>
                  <span>page_size</span>
                  <input
                    type='number'
                    min='1'
                    max='100'
                    value={form.page_size}
                    onChange={(e) => handleChange('page_size', e.target.value)}
                  />
                </label>
              </>
            )}
          </div>

          <div className='cost-actions'>
            <button
              type='button'
              className='cost-primary-btn'
              onClick={handleQuery}
              disabled={loading}
            >
              {loading ? '查询中...' : '立即查询'}
            </button>
            <button
              type='button'
              className='cost-secondary-btn'
              onClick={handleFillExample}
            >
              填充示例
            </button>
            <button
              type='button'
              className='cost-secondary-btn ghost'
              onClick={handleClear}
            >
              清空
            </button>
          </div>

          <div className='cost-request-preview'>
            <div className='cost-preview-header'>
              <div className='cost-preview-label'>当前请求</div>
              <div className='cost-inline-actions'>
                <button
                  type='button'
                  className='cost-inline-btn'
                  onClick={() => handleCopyText(requestPreview, '请求 URL')}
                >
                  复制 URL
                </button>
                <button
                  type='button'
                  className='cost-inline-btn'
                  onClick={() =>
                    handleCopyText(examplePayload.curl, 'cURL 示例')
                  }
                >
                  复制 cURL
                </button>
              </div>
            </div>
            <code>{requestPreview}</code>
          </div>
        </section>

        <section className='cost-doc-card'>
          <div className='cost-card-header'>
            <div>
              <h2>使用方法与注意事项</h2>
              <p>这里整理了最常见的调用方式、筛选建议和对外说明文案。</p>
            </div>
          </div>
          <ul className='cost-tip-list'>
            {activeDoc.tips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
          <div className='cost-mini-grid'>
            <div className='cost-mini-card'>
              <div className='cost-mini-title'>鉴权</div>
              <p>
                请求头使用{' '}
                <code>Authorization: Bearer YOUR_DISTRIBUTED_TOKEN</code>。
              </p>
            </div>
            <div className='cost-mini-card'>
              <div className='cost-mini-title'>时间参数</div>
              <p>
                {activeTab === 'summary'
                  ? '时间范围使用秒级 Unix 时间戳；开始时间留空时使用 token 创建时间，结束时间留空时使用当前时间。'
                  : '时间范围使用秒级 Unix 时间戳；留空时默认最近 30 天。'}
              </p>
            </div>
            <div className='cost-mini-card'>
              <div className='cost-mini-title'>返回边界</div>
              <p>
                只返回当前 token 自己的数据，不会暴露兄弟 token 的消费记录。
              </p>
            </div>
          </div>
        </section>
      </div>

      <section className='cost-example-card'>
        <div className='cost-card-header'>
          <div>
            <h2>调用示例与接入文档</h2>
            <p>
              可以直接复制下面的示例，替换 token 和查询参数后给下游用户使用。
            </p>
          </div>
        </div>
        <div className='cost-subtabs'>
          {EXAMPLE_TABS.map((tab) => (
            <button
              key={tab.id}
              type='button'
              className={`cost-subtab ${exampleTab === tab.id ? 'active' : ''}`}
              onClick={() => setExampleTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
          <button
            type='button'
            className='cost-subtab copy'
            onClick={() =>
              handleCopyText(examplePayload[exampleTab], `${exampleTab} 示例`)
            }
          >
            复制当前示例
          </button>
        </div>
        <pre className='cost-code-block'>
          <code>{examplePayload[exampleTab]}</code>
        </pre>
      </section>

      <section className='cost-result-card'>
        <div className='cost-card-header'>
          <div>
            <h2>查询结果</h2>
            <p>
              结构化视图适合人工阅读，JSON 视图适合直接复制给前端或集成脚本。
            </p>
          </div>
          <div className='cost-subtabs compact'>
            {RESULT_VIEWS.map((view) => (
              <button
                key={view.id}
                type='button'
                className={`cost-subtab ${
                  resultView === view.id ? 'active' : ''
                }`}
                onClick={() => setResultView(view.id)}
              >
                {view.label}
              </button>
            ))}
          </div>
        </div>
        {resultView === 'json' ? (
          <pre className='cost-code-block result'>
            <code>
              {JSON.stringify(result, null, 2) || '{\n  "success": true\n}'}
            </code>
          </pre>
        ) : (
          renderResultTable()
        )}
      </section>
    </div>
  );
};

export default CostQueryPanel;
